import { useAnalytics } from '@psyomics/ui-common';
import { GenericError, LoadingSpinner, SurveyJs } from '@psyomics/ui-components';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Model } from 'survey-react-ui';

import { LinkExpired } from '../../../app/routes/referral/views/link-expired';
import { useReferralInviteQuery } from '../../referral/hooks/use-referral-invite-query';
import { useSurveyResponse } from '../../survey-responses/hooks/use-survey-response';
import { useUpdateSurveyResponse } from '../../survey-responses/hooks/use-update-survey-response';

import { EndScreen } from './end-screen';
import { usePrepareSurvey } from './use-prepare-survey';

type SurveyData = Record<string, unknown>;

type SurveyBuilderProps = {
  readonly invitePublicId: string;
  readonly surveyJson: object;
  readonly surveyResponseId: string;
};

const createSurveyEventHandlers = ({
  analytics,
  invitePublicId,
  serviceCode,
  organisationCode,
  updateSurvey,
}: {
  analytics: ReturnType<typeof useAnalytics>;
  invitePublicId: string;
  serviceCode?: string | null;
  organisationCode?: string | null;
  updateSurvey: (survey: Model, complete: boolean) => Promise<void>;
}) => ({
  onPageChange: (survey: Model) => {
    if (survey.currentPage) {
      analytics.trackEvent({
        event: 'questionnaire_page_started',
        properties: {
          invite: invitePublicId,
          name: survey.currentPage.name,
          ...(serviceCode && { service: serviceCode }),
        },
        captureOptions: organisationCode ? { $groups: { organisation: organisationCode } } : undefined,
      });
    }
    updateSurvey(survey, false);
  },
  onComplete: (survey: Model) => {
    analytics.trackEvent({
      event: 'questionnaire_completed',
      properties: { invite: invitePublicId, ...(serviceCode && { service: serviceCode }) },
      captureOptions: organisationCode ? { $groups: { organisation: organisationCode } } : undefined,
    });
    updateSurvey(survey, true);
  },
  onValueChange: (survey: Model, options: { name: string }) => {
    const q = survey.getQuestionByName(options.name);
    const type = q.getType();
    if (type !== 'expression') {
      analytics.trackEvent({
        event: 'question_answered',
        properties: { invite: invitePublicId, name: options.name, ...(serviceCode && { service: serviceCode }) },
        captureOptions: organisationCode ? { $groups: { organisation: organisationCode } } : undefined,
      });
    }
  },
});

type UseSurveyUpdateQueueProps = {
  ready: boolean;
  setSurveyResults: (data: SurveyData) => void;
  surveyResponseId: string;
  surveyResponseUpdatedAt?: Date;
  updateSurveyAnswers: ReturnType<typeof useUpdateSurveyResponse>[1];
};

const useSurveyUpdateQueue = ({
  ready,
  setSurveyResults,
  surveyResponseId,
  surveyResponseUpdatedAt,
  updateSurveyAnswers,
}: UseSurveyUpdateQueueProps) => {
  const isUpdatingRef = useRef(false);
  const nextToUpdateRef = useRef<{ answersJson: string; complete: boolean } | null>(null);
  const latestUpdatedAtRef = useRef<Date | undefined>(surveyResponseUpdatedAt);

  useEffect(() => {
    latestUpdatedAtRef.current = surveyResponseUpdatedAt;
  }, [surveyResponseUpdatedAt]);

  const runNextUpdate = useCallback(async () => {
    if (isUpdatingRef.current) return;

    const next = nextToUpdateRef.current;
    if (!next || !surveyResponseId || !ready) return;

    nextToUpdateRef.current = null;
    isUpdatingRef.current = true;

    try {
      const result = await updateSurveyAnswers({
        input: {
          id: surveyResponseId,
          answers: next.answersJson,
          complete: next.complete,
          expectedUpdatedAt: latestUpdatedAtRef.current,
        },
      });

      const updatedAt = result.data?.updateSurveyResponse?.updatedAt;
      if (updatedAt) {
        latestUpdatedAtRef.current = new Date(updatedAt);
      }

      if (next.complete) {
        setSurveyResults(JSON.parse(next.answersJson));
      }
    } finally {
      isUpdatingRef.current = false;
    }

    if (nextToUpdateRef.current) void runNextUpdate();
  }, [ready, setSurveyResults, surveyResponseId, updateSurveyAnswers]);

  const enqueueUpdateSurvey = useCallback(
    async (survey: Model, complete: boolean) => {
      if (!surveyResponseId || !ready) return;
      nextToUpdateRef.current = { answersJson: JSON.stringify(survey.data), complete };
      if (!isUpdatingRef.current) void runNextUpdate();
    },
    [ready, runNextUpdate, surveyResponseId]
  );

  return enqueueUpdateSurvey;
};

type UseSurveyHandlerProps = UseSurveyUpdateQueueProps & {
  invitePublicId: string;
  organisationCode?: string | null;
  serviceCode?: string | null;
  surveyModel: Model;
};

const useSurveyHandlers = ({
  invitePublicId,
  organisationCode,
  ready,
  serviceCode,
  setSurveyResults,
  surveyModel,
  surveyResponseId,
  surveyResponseUpdatedAt,
  updateSurveyAnswers,
}: UseSurveyHandlerProps) => {
  const analytics = useAnalytics();

  const enqueueUpdateSurvey = useSurveyUpdateQueue({
    ready,
    setSurveyResults,
    surveyResponseId,
    surveyResponseUpdatedAt,
    updateSurveyAnswers,
  });

  useEffect(() => {
    const handlers = createSurveyEventHandlers({
      analytics,
      invitePublicId,
      organisationCode,
      serviceCode,
      updateSurvey: enqueueUpdateSurvey,
    });
    surveyModel.onCurrentPageChanged.add(handlers.onPageChange);
    surveyModel.onComplete.add(handlers.onComplete);
    surveyModel.onValueChanged.add(handlers.onValueChange);
    return () => {
      surveyModel.onValueChanged.clear();
      surveyModel.onCurrentPageChanged.clear();
      surveyModel.onComplete.clear();
    };
  }, [analytics, enqueueUpdateSurvey, invitePublicId, organisationCode, serviceCode, surveyModel]);
};

const RenderSurvey = memo(({ model }: { readonly model: SurveyJs.Model }) => <SurveyJs.SurveyRenderer model={model} />);

type ErrorTypes = 'SurveyNotFound' | 'SurveyResponseConflict';
const errorMap: Record<ErrorTypes, { titleKey: string; titleComponents?: Record<string, React.ReactElement> }> = {
  SurveyNotFound: { titleKey: 'page.viewReferral.errors.notFound' },
  SurveyResponseConflict: {
    titleKey: 'page.viewReferral.errors.conflict',
    titleComponents: {
      refreshLink: (
        <button
          className="text-blue-600 underline cursor-pointer"
          onClick={() => globalThis.location.reload()}
          type="button"
        >
          refresh
        </button>
      ),
    },
  },
};

export const SurveyBuilder: React.FC<SurveyBuilderProps> = ({ surveyJson, invitePublicId, surveyResponseId }) => {
  const [surveyResults, setSurveyResults] = useState<SurveyData | null>(null);
  const [{ errorMessage }, updateSurveyAnswers] = useUpdateSurveyResponse();
  const { data: surveyResponse, fetching: surveyResponseFetching } = useSurveyResponse(surveyResponseId);
  const [invite, , , reexecuteInviteQuery] = useReferralInviteQuery(invitePublicId);
  const { inviteExpired, organisationCode, serviceCode } = useMemo(() => {
    const referralSession = invite?.referralSession;
    const referral = referralSession?.referral;
    const organisation = referral?.organisation;
    const service = referral?.service;
    return {
      inviteExpired: referralSession?.status === 'expired',
      organisationCode: organisation?.code,
      serviceCode: service?.code,
    };
  }, [invite]);
  const surveyModel = useMemo(() => new Model(surveyJson), [surveyJson]);

  useEffect(() => {
    const handlePageChange = () => reexecuteInviteQuery({ requestPolicy: 'network-only' });
    surveyModel.onCurrentPageChanged.add(handlePageChange);
    return () => {
      surveyModel.onCurrentPageChanged.remove(handlePageChange);
    };
  }, [surveyModel, reexecuteInviteQuery]);

  const ready = usePrepareSurvey({
    surveyResponse,
    model: surveyModel,
    fetchingResponses: surveyResponseFetching,
  });

  useSurveyHandlers({
    invitePublicId,
    organisationCode,
    ready,
    serviceCode,
    setSurveyResults,
    surveyModel,
    surveyResponseId,
    surveyResponseUpdatedAt: surveyResponse?.updatedAt,
    updateSurveyAnswers,
  });

  if (!ready) return <LoadingSpinner />;
  if (inviteExpired) return <LinkExpired />;
  if (errorMessage) return <GenericError {...errorMap[errorMessage]} />;
  if (surveyResults || surveyResponse?.completedAt) return <EndScreen invitePublicId={invitePublicId} />;
  return (
    <div className="flex justify-center bg-neutral-100">
      <div className="w-full">
        <RenderSurvey model={surveyModel} />
      </div>
    </div>
  );
};
